import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';

import { useAnalytics, useResponsiveBreakPoints } from '@restworld/utils-common';
import { useWizard } from '@restworld/utils-common';
import { IconButton, Button } from '@restworld/ui-ds-base';

import { WizardExitConfirm } from '../wizard-step.components';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';
import { stepsMapping } from '../form-inputs.hooks';
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';

export const PreviousButton = ({ isMobile, entryTime }: { isMobile?: boolean; entryTime?: number }) => {
  const { isDesktop } = useResponsiveBreakPoints();
  const { previousStep, isFirstStep, activeStep, goToStep } = useWizard();
  const { setOverlayModalContent, fourthStepDecision, toggleUserModify } = useWorkerApplicationCxt();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation();
  const handleCloseBtn = useCallback(() => {
    if (isFirstStep) {
      setOverlayModalContent({
        visible: true,
        content: <WizardExitConfirm entryTime={entryTime} />,
      });
      return;
    }
    if (activeStep === 6 && fourthStepDecision === 'skip') {
      goToStep(4);
      return;
    }
    toggleUserModify(true);
    previousStep();
  }, [
    activeStep,
    entryTime,
    fourthStepDecision,
    goToStep,
    isFirstStep,
    previousStep,
    setOverlayModalContent,
    toggleUserModify,
  ]);

  const recordEvent = useCallback(() => {
    trackEvent('questionnaire_progress', {
      step_number: activeStep,
      step_name: stepsMapping[activeStep as number],
      time_spent: entryTime ? (Date.now() - entryTime) / 1000 : undefined,
      next_action: 'back',
    });
  }, [activeStep, entryTime, trackEvent]);

  return isMobile ? (
    <IconButton
      data-cy="previous-btn-mobile"
      ariaLabel="previous-btn-mobile"
      Icon={isFirstStep ? XMarkIcon : ChevronLeftIcon}
      onClick={() => {
        handleCloseBtn();
        recordEvent();
      }}
      variant="reversed"
    />
  ) : (
    <Button
      // master used `secondary-outline` (bordered); the ui-ds-base equivalent is `tertiary`.
      // `secondary-neutral` was a borderless grey fill that read as nearly invisible on the light panel.
      variant="tertiary"
      onClick={() => {
        handleCloseBtn();
        recordEvent();
      }}
      fullwidth={!isDesktop}
      data-cy="previous-btn-desktop"
    >
      {isFirstStep ? t('common:exit') : t('common:back')}
    </Button>
  );
};
